<template>
    <component
        :is="tag"
        class="cards-list-cards"
        :class="{
            [`cards-${cardCount}`]: true,
            [$cms.computedBackgroundClass(display, layout?.name)]: true,
            'inline': tag !== 'section'
        }"
    >
        <div class="inner">
            <div v-if="foodMenu && icons" class="legend">
                <span v-for="(allergen, i) in presentAllergens" :key="i"><DSFontIcon :code="allergen.code" size="xs" />
                    {{ $t(allergen.i18nText) }}
                </span>
            </div>
            <div
                v-if="title || cardCount === 'many'"
                class="heading"
                :class="{
                    'title-only': !$slots['external-link'] && cardCount !== 'many',
                }"
            >
                <div class="heading-content">
                    <h2 v-if="title" class="heading-title h3">{{ title }}</h2>
                    <p v-if="description" class="heading-description">{{ description }}</p>
                </div>
                <div class="heading-actions">
                    <div v-if="$slots['external-link']" class="external-link">
                        <slot name="external-link"></slot>
                    </div>
                    <div v-if="cardCount === 'many'" class="arrow-controls">
                        <button
                            class="left"
                            :aria-label="$t('button.left')"
                            :disabled="active < 1"
                            @click="prevSlide()"
                        >
                            <DSFontIcon code="arrow-left" icon-style="filled" icon-set="st1website" />
                        </button>
                        <button
                            class="right"
                            :aria-label="$t('button.right')"
                            :disabled="active === cards.length - 1"
                            @click="nextSlide()"
                        >
                            <DSFontIcon code="arrow-right" icon-style="filled" icon-set="st1website" />
                        </button>
                    </div>
                </div>
            </div>
            <component
                :is="cardCount === 'many' ? 'flicking' : 'div'"
                ref="flick"
                :options="computedOptions"
                class="cards"
                @changed="onChanged"
            >
                <div
                    v-for="(card, key) in cards"
                    :key="`card-${card.id}-${key}`"
                    class="card"
                    :class="{
                        contracted: !expanded.includes(`${card.id}-${key}`),
                    }"
                >
                    <div class="badges">
                        <div
                            v-for="productTag, i in (card?.tags ?? []).filter(isProductTag)"
                            :key="i"
                        >
                            <div class="tag-badge">
                                {{ $t(productTag) }}
                            </div>
                        </div>
                    </div>
                    <DSFrontifyAsset
                        v-if="card.image"
                        :asset="card.image"
                        :size="display.size"
                        fill="cover"
                    />
                    <div v-if="card.products?.length">
                        <BlocksCLProduct
                            :cards="[card]"
                            :block="false"
                            @add-product="addProduct"
                        />
                    </div>
                    <div v-else class="content">
                        <div
                            v-if="getCardExtras(card).length > 0"
                            class="byline"
                        >
                            <div v-if="getCardExtras(card)[0].specialties && icons" class="allergen-icons">
                                <DSFontIcon
                                    v-for="(spec, iconKey) in getCardExtras(card)[0].specialties"
                                    :key="`icon-${iconKey}`"
                                    :code="getSpecialty(spec)"
                                    size="xs"
                                />
                            </div>
                            <div v-else-if="getCardExtras(card)[0].specialties">
                                {{ getCardExtras(card)[0].specialties.join(', ') }}
                            </div>
                            <template v-else>
                                {{ getCardExtras(card).join(' | ') }}
                            </template>
                        </div>
                        <h3 v-if="card.title" class="h5">{{ card.title }}</h3>
                        <div class="expandable-text">
                            <div
                                v-if="card.text && typeof(card.text) === 'object'"
                                class="text"
                            >
                                <DSCFText
                                    :ref="comp => (textRefs[`${card.id}-${key}`] = comp?.$el)"
                                    :content="card.text"
                                    :class="{
                                        'full-width': !card.links,
                                    }"
                                />
                            </div>
                            <div v-else class="text">{{ card.text }}</div>
                            <button
                                v-if="hasExpand(`${card.id}-${key}`)"
                                class="expand"
                                :class="{
                                    'open': expanded.includes(`${card.id}-${key}`),
                                }"
                                @click="expand(`${card.id}-${key}`)"
                            >
                                <span>{{ expanded.includes(`${card.id}-${key}`) ? $t('showLess') : $t('showMore') }}</span>
                                <DSFontIcon code="chevron-down" icon-set="st1website" size="s" />
                            </button>
                        </div>
                        <div v-if="card?.price?.[0]?.price" class="prices">
                            <div
                                v-for="(price, pKey) in card.price"
                                :key="`price-${key}-${pKey}`"
                                class="item"
                            >
                                <span class="price">{{ $cms.formatPrice(price.price) }}</span>
                                <span class="title">{{ price.title }}</span>
                            </div>
                        </div>
                        <template v-if="$slots['after-content']">
                            <slot
                                name="after-content"
                                v-bind="{
                                    slotClass: 'after-content',
                                    card,
                                }"
                            ></slot>
                        </template>
                        <div v-if="card.links" class="buttons" :class="{'multiple-buttons': card.links?.length > 1}">
                            <NuxtLink
                                v-for="(link, lKey) in card.links.filter(link => link.title)"
                                :key="`link-${key}-${lKey}`"
                                class="ds-btn"
                                :class="{
                                    'ds-btn--primary': lKey === 0,
                                    'ds-btn--secondary': lKey > 0,
                                }"
                                v-bind="$cms.parseLink(link)"
                            >
                                <span class="label">{{ link.title }}</span>
                                <DSFontIcon
                                    v-if="link.icon || link.openInNewTab"
                                    :code="link.icon ? link.icon : 'externallink'"
                                    :icon-style="link.iconStyle ? link.iconStyle : 'line'"
                                    class="some-icon"
                                />
                            </NuxtLink>
                        </div>
                    </div>
                </div>
            </component>
        </div>
    </component>
</template>
<script setup>
import { computed, onMounted, onUnmounted, ref, inject, useI18n } from '#imports';
import allergens from '~dsc/utils/allergens.json';

const ROWS_BEFORE_EXPAND = 4;
const SAFETY_MARGIN = 3;
const active = ref(0);
const emits = defineEmits(['addProduct']);
const flick = ref(null);
const presentAllergens = ref([]);
const layout = inject('layout');

const expanded = ref([]);
const textRefs = ref({});

const textMaxHeight = computed(() => {
    const [firstTextElement] = Object.values(textRefs?.value ?? {}) || [];
    if (window && firstTextElement) {
        return `${Math.floor(Number(window.getComputedStyle(firstTextElement).lineHeight
            .replace(/px$/, ''))) * ROWS_BEFORE_EXPAND - SAFETY_MARGIN}px`;
    }

    return '';
});

const textMaxHeightRoof = computed(() => {
    if (window && textRefs.value) {

        return `${Math.max(...Object.values(textRefs.value)
            .map(item => Math.ceil(item?.getBoundingClientRect().height)))}px`;
    }

    return 'none';
});

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            button: {
                left: 'Nuoli vasen',
                right: 'Nuoli oikea',
            },
            showMore: 'Näytä lisää',
            showLess: 'Näytä vähemmän',
            productVegansk: 'Vegansk',
            productVeg: 'VEG',
            allergens: {
                glutenfree: 'Gluteenittomista raaka-aineista (lisätietoja henkilökunnalta)',
                lactosefree: 'Laktoositon',
                lowlactose: 'Vähälaktoosinen',
                milk: 'Maidoton',
                vegan: 'Vegaaninen',
            },
        },
        'en-EU': {
            button: {
                left: 'Arrow left',
                right: 'Arrow right',
            },
            showMore: 'Show more',
            showLess: 'Show less',
            productVegansk: 'Vegan',
            productVeg: 'VEG',
            allergens: {
                glutenfree: 'Glutenfree',
                lactosefree: 'Lactosefree',
                lowlactose: 'Low Lactose',
                milk: 'Milk',
                vegan: 'Vegan',
            },
        },
        'sv-SE': {
            button: {
                left: 'Pil vänster',
                right: 'Pil höger',
            },
            showMore: 'Visa mer',
            showLess: 'Visa mindre',
            productVegansk: 'Vegansk',
            productVeg: 'VEG',
            allergens: {
                glutenfree: 'Glutenfri',
                lactosefree: 'Laktosfri',
                lowlactose: 'Låglaktos',
                milk: 'Mjölkfri',
                vegan: 'Vegansk',
            },
        },
        'no-NO': {
            button: {
                left: 'Pil venstre',
                right: 'Pil høyre',
            },
            showMore: 'Vis mer',
            showLess: 'Vis mindre',
            productVegansk: 'Vegansk',
            productVeg: 'VEG',
            allergens: {
                glutenfree: 'Glutenfri',
                lactosefree: 'Laktosefri',
                lowlactose: 'Lav laktose',
                milk: 'Melk',
                vegan: 'Vegansk',
            },
        },
    },
});

const props = defineProps({
    contentType: {
        type: String,
        required: false,
        default: 'cardList',
    },
    internal: {
        type: String,
        required: false,
        default: '',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    countOverride: {
        type: [Number, String],
        required: false,
        default: '',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            component: 'cards',
            background: 'none',
        }),
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
    description: {
        type: String,
        required: false,
        default: '',
    },
    cardBackground: {
        type: String,
        required: false,
        default: 'transparent',
    },
    foodMenu: {
        type: Boolean,
        required: false,
        default: false,
    },
    products: {
        type: Array,
        required: false,
        default: () => ([]),
    },
    tag: {
        type: String,
        required: false,
        default: 'section',
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
});

onMounted(() => {

    window.addEventListener('resize', () => hasExpand());

    const foundAllergens = [];

    for (const card of props.cards) {
        if (card?.byline?.specialties) {
            for (const allergen of allergens) {
                if (card.byline.specialties.includes(allergen.shorthand)) {
                    foundAllergens.push(allergen);
                }
            }
        }
    }

    presentAllergens.value = foundAllergens;
});

onUnmounted(() => window.removeEventListener('resize', () => hasExpand()));

const cardCount = computed(() => {
    if (props.countOverride) {
        return props.countOverride;
    } else if (props.cards.length < 5) {
        return props.cards.length;
    }

    return 'many';
});

const computedOptions = computed(() => {
    if (cardCount.value === 'many') {
        return {
            align: 'prev',
            circular: false,
            bound: true,
        };
    }

    return null;
});

const icons = computed(() => {
    const [, domain] = this.$store.getters['layout/getHost'].split('//');
    const [subdomain, site] = domain.split('.');
    const usesAllergenIcons = [
        'ploq',
        'helmisimpukka',
    ];

    if (usesAllergenIcons.includes(site) || usesAllergenIcons.includes(subdomain)) {
        return true;
    }

    return false;
});

const hasExpand = id => {
    const targetElement = textRefs?.value?.[id];
    if (window && targetElement) {
        const maxHeight = Math.ceil(Number(window.getComputedStyle(targetElement).lineHeight
            .replace(/px$/, '')) * ROWS_BEFORE_EXPAND);
        const height = Math.round(targetElement.getBoundingClientRect().height);

        if (height > maxHeight) {
            return true;
        }
    }

    return false;
};

function addProduct(product) {
    emits('addProduct', product);
}

function prevSlide() {
    flick.value.prev().catch(err => {
        if (err.name === 'FlickingError') return;
        throw err;
    });
}

function nextSlide() {
    flick.value.next().catch(err => {
        if (err.name === 'FlickingError') return;
        throw err;
    });
}

function onChanged(e) {
    active.value = e.index;
}

function getCardExtras(card) {
    const extras = [];

    if (card.byline) {
        extras.push(card.byline);
    }

    return extras;
}

function getSpecialty(spec) {
    switch (spec) {
        case 'G':
            return 'allergens-glutenfree-filled';
            break;
        case 'L':
            return 'allergens-lactosefree-filled';
            break;
        case 'M':
            return 'milk';
            break;
        case 'VL':
            return 'low-lactose';
            break;
        case 'V':
            return 'vegan';
            break;
    }

    return 'error';
}

function isProductTag(productTag) {
    return [
        'productVegansk',
        'productVeg',
    ].includes(productTag);
}

function expand(id) {
    if (expanded.value.includes(id)) {
        return expanded.value = expanded.value.filter(expandedId => expandedId !== id);
    }
    expanded.value.push(id);

}

</script>
<style>
.cards-list-cards .byline {
  font-size: 0.875rem;
}
</style>
<style lang="scss" scoped>
@import '../../../assets/scss/mixins';

.cards-list-cards {
    @include block-level-component();

    &:not(.inline) {
        padding-block: 4rem;
    }

    .image-container {
        align-self: start;
        grid-column-start: 1;
        grid-row-start: 1;
        height: 4rem;
    }


    &.bg-custom {
        .inner .heading + .cards {
            margin-top: 2rem;
        }

        .card {
            background-color: v-bind('display.background');
        }
    }

    &.bg-primary {
        .card {
            background: var(--color-primary-500);
        }
    }

    &.bg-secondary {
        .card {
            background: var(--color-secondary-50);
        }
    }

    &.bg-primary, &.bg-secondary, &.bg-custom {
        .content {
            padding: 0 1.5rem 1rem;
        }
    }

    &.cards-1 {
        .cards {
            display: grid;
            grid-template-columns: 1fr;
            grid-gap: 1.5rem 0;
        }

        .card {
            grid-template-columns: 265px 1fr;
            grid-template-rows: 1fr;
        }

        .image-container {
            padding-top: 93.44%;
            margin-bottom: 0;
        }

        .buttons {
            display: inline-flex;
            flex-flow: row wrap;
            align-self: flex-start;
        }

        .content {
            display: flex;
            flex-flow: column wrap;
            padding: 0 1.5rem;
        }

        @include respond-to('at most small desktop') {
            .card {
                grid-template-columns: 1fr;
                grid-template-rows: repeat(2, auto);

                .content {
                    padding: 0 0 1.5rem;
                }
            }
        }
    }

    &.cards-2,
    &.cards-3,
    &.cards-4 {
        .cards {
            display: grid;
            grid-gap: 0 1.5rem;
        }
    }

    &.cards-2 {
        .cards {
            grid-template-columns: repeat(2, 1fr);
        }

        .image-container {
            padding-top: 36%;
        }
    }

    &.cards-3 {
        .cards {
            grid-template-columns: repeat(3, 1fr);
        }

        .image-container {
            padding-top: 56.25%;
        }
    }

    &.cards-4 {
        .cards {
            grid-template-columns: repeat(4, 1fr);
        }

        .image-container {
            padding-top: 75%;
        }
    }

    &.cards-many {
        .card {
            min-width: calc(25% - .75rem);
            margin-right: 1rem;

            @include respond-to('at most small desktop') {
                min-width: calc(50% - 0.75rem);
            }

            @include respond-to('at most large mobile') {
                margin-right: 0;
                min-width: 100%;
            }
        }


        .inner {
            margin: 0 auto;
            width: 100dvw;
            position: relative;
        }

        .image-container {
            padding-top: 56.25%;
        }

        .arrow-controls {
            button {
                @include default-transition();
                border-radius: 50%;
                width: 3rem;
                height: 3rem;
                background: var(--color-primary-500);
                color: var(--color-base-white);
                display: inline-flex;
                align-items: center;
                justify-content: center;

                &:first-child {
                    margin-right: .25rem;
                }

                &:disabled {
                    opacity: .5;
                }
            }

            &:first-child {
                margin-left: auto;
            }
        }
    }

    &.inline {
        padding-bottom: 0;

        .inner {
            padding-left: 0;
            padding-right: 0;
            max-width: calc(var(--page-max-width) - 3.5rem);
        }
    }
}

.heading {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    margin-bottom: 1rem;
}

.heading-description {
    margin-top: 1rem;
    font-weight: 700;
}

.h3 {
    margin-bottom: 0;
    padding-right: .5rem;
}

.heading-actions {
    display: flex;
    justify-content: space-between;
    padding-left: 1rem;
    margin-left: auto;
}

.external-link {
    margin-left: auto;
    margin-right: 2.5rem;
}

.h5 {
    margin-top: .5rem;
    margin-bottom: .5rem;

    &:first-child {
        margin-top: 2rem;
    }
}

.byline {
    display: flex;
    line-height: 1.33em;
    text-transform: uppercase;
    margin-top: 1rem;
}

.prices {
    order: 3;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 1rem;
    margin-top: auto;
    padding-top: .75rem;
    max-width: 100%;
    justify-self: end;

    .item {
        display: flex;
        flex-flow: row wrap;
        position: relative;

        span {
            display: flex;
            flex-basis: 100%;
        }

        .price {
            font-weight: 800;
            font-size: 1.5rem;
            margin-bottom: .125rem;
            white-space: nowrap;
        }

        .title {
            font-weight: 700;
            margin-top: auto;
            justify-self: end;
            color: var(--color-base-grey-500);
            overflow-x: hidden;
        }

        &:not(:first-child):not(:nth-child(3n+1))::before {
            content: '';
            width: 1px;
            height: 100%;
            left: calc(-.5rem - 1px);
            position: absolute;
            background: var(--color-base-grey-300);
        }
    }
}

.expand {
    margin-top: .5rem;
    font-weight: 800;
    justify-self: start;
    align-self: start;

    .icon {
        @include default-transition();
        margin-left: .5rem;
    }
}

.buttons {
    margin-top: 1.5rem;
    display: inline-flex;
    align-self: end;
    flex-wrap: wrap;
    grid-row-start: -1;

    .ds-btn {
        margin-bottom: .5rem;
        white-space: nowrap;

        &:not(:last-child) {
            margin-right: 1rem;
        }
    }
}

.badges {
    position: relative;
    top: 1rem;
    left: 1rem;
    z-index: 2;
    height: 0;
    grid-column-start: 1;
    grid-row-start: 1;
}

.tag-badge {
    display: inline-block;
    padding: .25rem .5rem;
    background: var(--color-success-400);
    color: var(--color-base-white);
    text-transform: uppercase;
}

.cards-list-cards.product-cards {
    .card {
        display: grid;
        grid-template-rows: auto 1fr;
        overflow: hidden;
        border-radius: 3px;
        align-items: start;
    }

    .content {
        display: flex;
        flex-flow: column wrap;
        height: 100%;
        padding: 1rem;
    }

    .card--none .content {
        padding: 1rem 0;
    }

    .text {
        order: 1;
        margin: auto;
    }

    .h5 {
        order: 0;
        margin-top: 0;
    }

    .image-container + .content {
        border-width: 0 1px 1px;
    }

    .byline {
        font-weight: 700;
        color: var(--color-base-black);
        margin: 1rem 0 0;
        order: 2;

        .allergen-icons {
            span {
                margin: .25rem;
            }
        }
    }
}

.cards-list-cards.article-cards {
    .card {
        display: grid;
        grid-template-rows: auto 1fr;
        overflow: hidden;
        border: 0 solid var(--color-base-grey-200);
        border-radius: 3px;

        .content {
            padding: 1rem 0;
        }

        .text {
            margin: 1rem 0 0;
            order: 2;

        }

        .buttons {
            order: 3;
        }
    }

    .content {
        display: flex;
        flex-flow: column wrap;
        padding: 1rem;
    }

    .h5 {
        margin-top: 0;
        order: 1;
    }

    .image-container + .content {
        border-width: 0 1px 1px;
    }

    .byline {
        font-weight: 700;
        color: var(--color-base-grey-500);
        margin: 1rem 0;
        order: 0;
    }
}

@include respond-to('at most mobile') {
    .cards-list-cards {
        &.cards-2,
        &.cards-3,
        &.cards-4 {
            .cards {
                grid-template-columns: 1fr;
            }
        }

        &.cards-3 {
            .cards {
                gap: 1rem;
            }
        }
    }

    .external-link {
        margin-left: 0;
    }

    .h3 {
        flex-basis: 100%;
    }
}

@include respond-to('at most large mobile') {
    .cards-list-cards {
        &.cards-2,
        &.cards-4 {
            .image-container {
                padding-top: 56.25%;
            }
        }

        &.cards-4 {
            .cards {
                grid-template-columns: repeat(1, 1fr);
            }
        }
    }

    .h3 {
        font-size: 1.5rem;
    }
}

@include respond-to('at least large mobile') {
    .heading {
        display: grid;
        grid-column-gap: 1rem;

        &:not(.title-only) {
            grid-template-columns: 1fr auto;
        }
    }

    .heading-actions {
        flex-direction: row;
        margin-bottom: 0;
    }
}

@include respond-to('at most small desktop') {
    .prices {
        padding-top: 1.5rem;

        .item {
            justify-self: stretch;

            .price {
                font-size: 1rem;
            }

            .title {
                display: flex;
                font-size: .875rem;
                max-width: 100%;
                font-weight: 500;
            }
        }
    }
}

.card {
    &--white {
        background-color: var(--color-base-white);
        border: 1px solid var(--color-base-grey-200);
    }
}

:deep(.flicking-camera) {
    display: flex;
    flex-flow: row nowrap;
    height: auto;
}

.card {
    display: grid;
    grid-row: span 5;
    grid-template-rows: subgrid;
    align-self: stretch;
    margin-bottom: 1.5rem;

    .cover {
        width: 100%;
    }

    .content {
        display: grid;
        grid-row: span 5;
        grid-template-rows: subgrid;
        grid-gap: 0;
        overflow: hidden;

        .text {
            flex-grow: 1;
            width: 100%;
        }
    }

    &.contracted {
        .text {
            @include default-transition();
            max-height: v-bind(textMaxHeight);
            overflow: hidden;
        }
    }

    &:not(.contracted) {
        .text {
            @include default-transition();
            max-height: v-bind(textMaxHeightRoof);
            overflow: hidden;
        }

        .expand .icon {
            transform: rotate(-180deg);
            display: inline-block;
            margin-top: 0;
        }
    }
}

.legend {
    display: flex;
    flex-wrap: wrap;
    margin: 1rem 0;
    gap: 1rem;

    span {
        display: grid;
        grid-template-columns: 1fr auto;
    }

    .icon {
        align-self: center;
        margin-left: 0;
        margin-right: .5rem;
    }
}

:deep(.after-content) {
    margin-top: 1.5rem;
}
</style>
